import { useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';

import { Endpoints } from '@/api';
import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import {
  BrandRequestMainContent,
  BrandRequestMainFooter,
} from '@/layouts/Brand/BrandRequestLayout/BrandRequestLayout';
import { useArticleName } from '@/models/article';
import {
  CheckoutData,
  isInPaymentStep,
  RequestWithRelations,
  usePayment,
  usePaymentChoice,
  useRequest,
  useStartPayment,
} from '@/models/request';
import { ClientArticlesList } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList';
import { Choices } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList/components/ArticleNameWithChoice';
import ClientStepper from '@/routes/Brand/Requests/Request/components/shared/ClientStepper';
import { PickupPointInfo } from '@/routes/Brand/Requests/Request/components/shared/PickupPointInfo/PickupPointInfo';
import { useStripe } from '@/services/stripe';
import useViewPort from '@/utils/useViewport';

import './PaymentLegacy.css';

const PaymentLegacy = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();

  const { data: payment, isLoading: isLoadingPayment } = usePayment({ requestId: request.id });
  const { mutateAsync: startPayment } = useStartPayment();
  const [paymentError, setPaymentError] = useState<string>();
  const [checkoutData, setCheckoutData] = useState<CheckoutData>();
  const isReturnFromCheckout = new URLSearchParams(window.location.search).get('return');

  if (isLoadingPayment) {
    return (
      <BrandRequestMainContent>
        <Loading>
          <Trans id="_general.loading">Loading...</Trans>
        </Loading>
      </BrandRequestMainContent>
    );
  }

  if (
    isReturnFromCheckout !== null ||
    payment?.status !== 'unstarted' ||
    checkoutData?.connector === 'mock'
  ) {
    return (
      <BrandRequestMainContent>
        <PaymentStatus request={request} />
      </BrandRequestMainContent>
    );
  }

  if (checkoutData?.connector === 'stripe' && checkoutData?.stripe.clientSecret) {
    return (
      <BrandRequestMainContent>
        <StripePaymentCheckout clientSecret={checkoutData.stripe.clientSecret} />
      </BrandRequestMainContent>
    );
  }

  return (
    <PaymentWelcome
      request={request}
      onAccept={({ shouldStartPayment }: { shouldStartPayment: boolean }) => {
        if (shouldStartPayment) {
          startPayment({ requestId: request.id })
            .then(setCheckoutData)
            .catch((err) => {
              console.error(err);
              setPaymentError(
                (err.message as string) ??
                  t({ id: '_general.error.unknown', message: 'Unknown error' })
              );
            });
        }
      }}
      paymentError={paymentError}
    />
  );
};

const PaymentWelcome = ({
  request,
  onAccept,
  paymentError,
}: {
  request: RequestWithRelations;
  onAccept: ({ shouldStartPayment }: { shouldStartPayment: boolean }) => void;
  paymentError?: string;
}) => {
  const {
    mutateAsync: sendPaymentChoice,
    isPending: isPendingSendPaymentChoice,
    isSuccess: isSuccessSendPaymentChoice,
  } = usePaymentChoice();
  const { isMobile } = useViewPort();

  const activeArticlesInPaymentStep = request.articles.filter(
    (article) => article.step?.step === 'payment' && !article.cancelledAt
  );

  const [choices, setChoices] = useState<Choices>(
    activeArticlesInPaymentStep.reduce(
      (acc, article) => ({
        ...acc,
        [article.id]: {
          value: 'accepted',
          reason: null,
          otherReason: null,
        },
      }),
      {} as Choices
    )
  );
  const [showChoicesError, setShowChoicesError] = useState(false);
  const reasonMissing = Object.values(choices).some(
    (choice) =>
      choice.value === 'refused' &&
      (!choice.reason || (choice.reason === 'other' && !choice.otherReason))
  );
  const [error, setError] = useState<string | null>(null);

  const firstArticleName = useArticleName({
    article: activeArticlesInPaymentStep[0],
    type: 'short',
  });

  const priceComponents = request.organizationPrice?.components.filter(
    (component) =>
      component.subType === 'fee' ||
      (component.subType === 'article' &&
        activeArticlesInPaymentStep.some((article) => article.id === component.componentId))
  );

  const isFree = priceComponents?.every(
    (priceAggregate) =>
      priceAggregate.amountPerCurrency?.reduce((acc, amount) => acc + amount.amount, 0) === 0
  );

  const hasRefusedAllChoices = Object.values(choices).every((choice) => choice.value === 'refused');

  const acceptedPriceComponents = priceComponents?.filter(
    (component) =>
      component.subType === 'fee' ||
      (component.subType === 'article' && choices[component.componentId].value === 'accepted')
  );

  const areChoicesFree =
    hasRefusedAllChoices ||
    acceptedPriceComponents?.every(
      (priceAggregate) =>
        priceAggregate.amountPerCurrency?.reduce((acc, amount) => acc + amount.amount, 0) === 0
    );

  const isInternalPayment = activeArticlesInPaymentStep.find(
    (article) => article.step?.step === 'payment' && article.step?.config.method !== 'external'
  );

  // This can happen if `startPayment` fails and the user refreshes the page
  const allArticlesAlreadyHaveAPaymentChoice = activeArticlesInPaymentStep.every(
    (article) =>
      article.paymentAcceptedAt || article.paymentRefusedAt || article.freeFinalQuoteAcceptedAt
  );
  const shouldShowChoices = !allArticlesAlreadyHaveAPaymentChoice && isInternalPayment;

  function onSubmit() {
    if (allArticlesAlreadyHaveAPaymentChoice) {
      onAccept({ shouldStartPayment: true });

      return;
    }

    if (reasonMissing) {
      setShowChoicesError(true);

      return;
    }

    setError(null);

    sendPaymentChoice({
      id: request.id,
      choices: choices as Endpoints['POST /requests/:id/payment-choice']['body']['choices'],
    })
      .then(() => {
        onAccept({ shouldStartPayment: !areChoicesFree });
      })
      .catch((error) => {
        setError(error.message as string);
      });
  }

  return (
    <>
      <BrandRequestMainContent>
        <Stack gap="1.5rem">
          <ClientStepper request={request} />
          <Stack gap="0.5rem" style={{ marginBottom: '1rem' }}>
            <Title size={isMobile ? '300' : '200'} as="h1">
              {isFree ? (
                <Trans id="client.request.payment.welcome.title.free">Care and repair quote</Trans>
              ) : (
                <Trans id="client.request.payment.welcome.title.paying">
                  A payment is required from your side.
                </Trans>
              )}
            </Title>
            <Paragraph size={isMobile ? '100' : '50'}>
              <Trans id="client.request.payment.welcome.description">
                Our experts have confirmed the required actions for{' '}
                <Plural
                  value={activeArticlesInPaymentStep.length}
                  one={
                    <span>
                      your{' '}
                      <InlineText size={isMobile ? '100' : '50'} weight="medium">
                        {firstArticleName}
                      </InlineText>
                    </span>
                  }
                  other={
                    <span>
                      your{' '}
                      <InlineText size={isMobile ? '100' : '50'} weight="medium">
                        # items
                      </InlineText>
                    </span>
                  }
                />
                .
              </Trans>{' '}
              {isInternalPayment && !isFree && (
                <Trans id="client.request.payment.welcome.please">
                  Please now proceed to payment.
                </Trans>
              )}
              {isFree && (
                <b>
                  <Trans id="client.request.payment.welcome.free">
                    And good news, no payment will be required on your side!
                  </Trans>
                </b>
              )}
            </Paragraph>
          </Stack>
          <Stack gap="1rem">
            <PickupPointInfo request={request} />
            <ClientArticlesList
              request={request}
              showPrice
              showArticleComment
              choices={shouldShowChoices ? choices : undefined}
              setChoice={
                shouldShowChoices
                  ? (articleId, choice) => setChoices?.({ ...choices, [articleId]: choice })
                  : undefined
              }
              showChoicesError={shouldShowChoices ? showChoicesError : undefined}
            />
          </Stack>
          {paymentError && (
            <Message type="error" style={{ marginTop: '2rem' }}>
              <b>{paymentError}</b>
            </Message>
          )}
        </Stack>
      </BrandRequestMainContent>
      {isInternalPayment && (
        <BrandRequestMainFooter>
          <Stack gap="0.5rem" style={{ flex: 1 }}>
            {showChoicesError && (reasonMissing || error) && (
              <Paragraph weight="medium" color="danger" align="center">
                {reasonMissing ? (
                  <Trans id="client.request.payment.error.reason-missing">
                    Please select a reason for each refusal
                  </Trans>
                ) : (
                  error
                )}
              </Paragraph>
            )}
            <Button
              variant="brand"
              size="large"
              onPress={onSubmit}
              disabled={isPendingSendPaymentChoice || isSuccessSendPaymentChoice}
              isLoading={isPendingSendPaymentChoice || isSuccessSendPaymentChoice}
            >
              {areChoicesFree || allArticlesAlreadyHaveAPaymentChoice ? (
                <Trans id="client.request.payment.welcome.accept.free">Confirm</Trans>
              ) : (
                <Trans id="client.request.payment.welcome.accept">
                  Confirm & Proceed to payment
                </Trans>
              )}
            </Button>
          </Stack>
        </BrandRequestMainFooter>
      )}
    </>
  );
};

const StripePaymentCheckout = ({ clientSecret }: { clientSecret: string }) => {
  const stripe = useStripe();

  if (!stripe) {
    return (
      <Loading>
        <Trans id="_general.loading">Loading...</Trans>
      </Loading>
    );
  }

  return (
    <Stack padding="16px" style={{ flex: 1 }}>
      {clientSecret && (
        <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret }}>
          <EmbeddedCheckout className="stripe-checkout" />
        </EmbeddedCheckoutProvider>
      )}
    </Stack>
  );
};

const PaymentStatus = ({ request }: { request: RequestWithRelations }) => {
  /**
   * We refetch the request every second to check if the request is still in the Payment step.
   * Once the payment succeeds, the articles will be moved to their next step.
   * Therefore, they won't be in the Payment step anymore, and the `Payment` component will be unmounted.
   */
  useRequest(request.id, {
    refetchInterval: ({ state: { data } }) => (data && isInPaymentStep(data) ? 1000 : false),
  });

  return (
    <Loading>
      <Trans id="client.request.payment.in-progress">
        Your payment is being processed, please wait...
      </Trans>
    </Loading>
  );
};

const Loading = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      style={{ height: '100%' }}
      gap="1rem"
      alignItems="center"
      flexWrap="nowrap"
      justifyContent="center"
    >
      <Loader style={{ height: '40px', width: '40px' }} />
      <Paragraph>{children}</Paragraph>
    </Stack>
  );
};

export default PaymentLegacy;
